
// =========================================================
// LA ESQUINA DEL PARQUÉS
// REGISTER.jsx
// REGISTRO PREMIUM · NEON RETRO
// =========================================================

import React, {
  useState,
  useCallback,
  useEffect,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  Eye,
  EyeOff,
  Loader2,
  LockKeyhole,
  Mail,
  UserRound,
  AlertTriangle,
  ArrowRight,
  Crown,
  Check,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import API from "../../services/api";

import Navbar from "../../components/common/Navbar";
import Footer from "../../components/common/Footer";

import logo from "../../assets/logo.png";
import bg from "../../assets/bg.png";

import "../../styles/register.css";

export default function Register() {
  const navigate = useNavigate();

  // =======================================================
  // FORMULARIO
  // =======================================================

  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
  });

  // =======================================================
  // ESTADOS
  // =======================================================

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mounted, setMounted] = useState(false);

  // =======================================================
  // ANIMACIÓN INICIAL
  // =======================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
    }, 50);

    return () => clearTimeout(timer);
  }, []);

  // =======================================================
  // CAMBIO DE CAMPOS
  // =======================================================

  const handleChange = useCallback((event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));

    setError("");
  }, []);

  // =======================================================
  // VALIDACIÓN
  // =======================================================

  const validateForm = useCallback(() => {
    const username = form.username.trim();
    const email = form.email.trim();
    const password = form.password;

    if (!username) {
      setError("Ingresa tu nombre de usuario.");
      return false;
    }

    if (username.length < 3) {
      setError(
        "El usuario debe tener al menos 3 caracteres."
      );
      return false;
    }

    if (!email) {
      setError("Ingresa tu correo electrónico.");
      return false;
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      setError("Ingresa un correo electrónico válido.");
      return false;
    }

    if (!password) {
      setError("Ingresa una contraseña.");
      return false;
    }

    if (password.length < 6) {
      setError(
        "La contraseña debe tener al menos 6 caracteres."
      );
      return false;
    }

    return true;
  }, [form]);

  // =======================================================
  // REGISTRO
  // =======================================================

  const registerRequest = useCallback(async () => {
    if (loading) return;

    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      const response = await API.post(
        "auth/register/",
        {
          username: form.username.trim(),
          email: form.email.trim(),
          password: form.password,
        }
      );

      console.log(
        "REGISTER SUCCESS:",
        response.data
      );

      navigate("/login", {
        replace: true,
        state: {
          message:
            "Cuenta creada correctamente. Ya puedes iniciar sesión.",
        },
      });
    } catch (err) {
      console.error(
        "REGISTER ERROR:",
        err.response?.data || err
      );

      const data = err.response?.data || {};
      const backendErrors = data.errors || {};

      setError(
        backendErrors.username?.[0] ||
          backendErrors.email?.[0] ||
          backendErrors.password?.[0] ||
          backendErrors.non_field_errors?.[0] ||
          data.message ||
          data.detail ||
          data.error ||
          "No fue posible crear tu cuenta. Verifica los datos e inténtalo nuevamente."
      );
    } finally {
      setLoading(false);
    }
  }, [
    loading,
    validateForm,
    form,
    navigate,
  ]);

  // =======================================================
  // ENTER PARA ENVIAR
  // =======================================================

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        registerRequest();
      }
    },
    [registerRequest]
  );

  // =======================================================
  // VOLVER AL LOGIN
  // =======================================================

  const goToLogin = useCallback(() => {
    if (loading) return;

    navigate("/login");
  }, [loading, navigate]);

  // =======================================================
  // RENDER
  // =======================================================

  return (
    <div
      className={`register-page ${
        mounted ? "register-page--visible" : ""
      }`}
      style={{
        "--register-bg": `url("${bg}")`,
      }}
    >
      {/* ===================================================
          FONDO Y EFECTOS
      =================================================== */}

      <div className="register-page__overlay" />
      <div className="register-page__noise" />
      <div className="register-page__scanlines" />

      {/* ===================================================
          NAVBAR
      =================================================== */}

      <Navbar />

      {/* ===================================================
          CONTENIDO PRINCIPAL
      =================================================== */}

      <main className="register-main">
        <section
          className="register-card"
          aria-labelledby="register-title"
        >
          {/* Decoración */}

          <div className="register-card__top-line" />

          <div className="register-card__corner register-card__corner--tl" />
          <div className="register-card__corner register-card__corner--tr" />
          <div className="register-card__corner register-card__corner--bl" />
          <div className="register-card__corner register-card__corner--br" />

          {/* =================================================
              LOGO Y ENCABEZADO
          ================================================= */}

          <header className="register-card__header">
            <div className="register-logo-wrap">
              <div className="register-logo-crown">
                <Crown size={27} />
              </div>

              <img
                src={logo}
                alt="La Esquina del Parqués"
                className="register-logo"
              />
            </div>

            <div className="register-heading">
              <div className="register-heading__eyebrow">
                <Sparkles size={14} />

                <span>
                  LA ESQUINA DEL PARQUÉS
                </span>
              </div>

              <h1 id="register-title">
                CREA TU CUENTA
              </h1>

              <p>
                ¡Únete a la comunidad y que comience la partida!
              </p>
            </div>
          </header>

          {/* =================================================
              FORMULARIO
          ================================================= */}

          <form
            className="register-form"
            onSubmit={(event) => {
              event.preventDefault();
              registerRequest();
            }}
            noValidate
          >
            {/* Usuario */}

            <label
              className={`register-input ${
                form.username ? "register-input--filled" : ""
              }`}
            >
              <UserRound
                size={20}
                className="register-input__icon"
              />

              <input
                type="text"
                name="username"
                value={form.username}
                placeholder="Nombre de usuario"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="username"
                aria-label="Nombre de usuario"
                minLength={3}
                maxLength={150}
                required
                autoFocus
              />
            </label>

            {/* Correo */}

            <label
              className={`register-input ${
                form.email ? "register-input--filled" : ""
              }`}
            >
              <Mail
                size={20}
                className="register-input__icon"
              />

              <input
                type="email"
                name="email"
                value={form.email}
                placeholder="Correo electrónico"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="email"
                aria-label="Correo electrónico"
                maxLength={254}
                required
              />
            </label>

            {/* Contraseña */}

            <label
              className={`register-input ${
                form.password ? "register-input--filled" : ""
              }`}
            >
              <LockKeyhole
                size={20}
                className="register-input__icon"
              />

              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                placeholder="Crea una contraseña"
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                disabled={loading}
                autoComplete="new-password"
                aria-label="Contraseña"
                minLength={6}
                required
              />

              <button
                type="button"
                className="register-input__toggle"
                onClick={() => {
                  setShowPassword((previous) => !previous);
                }}
                disabled={loading}
                aria-label={
                  showPassword
                    ? "Ocultar contraseña"
                    : "Mostrar contraseña"
                }
              >
                {showPassword ? (
                  <EyeOff size={19} />
                ) : (
                  <Eye size={19} />
                )}
              </button>
            </label>

            {/* Requisito de contraseña */}

            <div className="register-password-info">
              <div className="register-password-info__icon">
                <Check size={13} />
              </div>

              <span>
                Tu contraseña debe tener al menos 6 caracteres.
              </span>
            </div>

            {/* Error */}

            {error && (
              <div
                className="register-error"
                role="alert"
                aria-live="polite"
              >
                <AlertTriangle size={18} />
                <span>{error}</span>
              </div>
            )}

            {/* Botón de registro */}

            <button
              type="submit"
              className="register-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2
                    size={20}
                    className="register-spinner"
                  />

                  <span>
                    CREANDO CUENTA...
                  </span>
                </>
              ) : (
                <>
                  <span>
                    CREAR MI CUENTA
                  </span>

                  <ArrowRight size={21} />
                </>
              )}
            </button>

            {/* Seguridad */}

            <div className="register-security">
              <ShieldCheck size={17} />

              <span>
                Tus datos se mantienen protegidos.
              </span>
            </div>

            {/* Divisor */}

            <div className="register-divider">
              <span />

              <b>
                BIENVENIDO A LA ESQUINA
              </b>

              <span />
            </div>

            {/* Acceso al login */}

            <div className="register-login">
              <span>
                ¿Ya tienes una cuenta?
              </span>

              <button
                type="button"
                onClick={goToLogin}
                disabled={loading}
              >
                Iniciar sesión
              </button>
            </div>
          </form>

          {/* =================================================
              PIE DE LA TARJETA
          ================================================= */}

          <div className="register-card__bottom">
            <span>BUENOS MOMENTOS</span>

            <i>✦</i>

            <span>GRANDES PARTIDAS</span>
          </div>
        </section>
      </main>

      {/* ===================================================
          FOOTER GLOBAL
      =================================================== */}

      <Footer />
    </div>
  );
}
